import { arrayHelpers, compareHelpers } from '@approvalmax/utils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { Company, CompanyMe, useGetCompanies, useGetCompany } from 'shared/data/webApp/v2';
import { isEnoughDaysAfter } from 'shared/helpers';
import { getCanShowPushingConnectGLPopupV2, getCanShowSubscribeAfterTrialButtonV2 } from 'shared/helpers/experiments';
import { useUserBetaFeatures } from 'shared/hooks';
import { activeCompanyIdState, pushingConnectGLPopupHideState } from 'shared/states';

import { pushingConnectGLPopupState } from '../PushingConnectGLPopup/PushingConnectGLPopup.states';
import { subscribeAfterTrialPopupOpenState } from '../SubscribeAfterTrialPopup/SubscribeAfterTrialPopup.states';
import {
    maxShowCountForPushingConnectGLPopup,
    sessionKeyAfterTrialBigPopupShowCount,
    sessionKeyAfterTrialBigPopupShowTime,
    sessionKeyPushingConnectGLShowCount,
    sessionKeyPushingConnectGLShowTime,
    storageKeyPushingConnectGLShowCount,
} from './Drawer.constants';
import { messages } from './Drawer.messages';
import { UseCountMenuClickCallbackParams } from './Drawer.types';

export const useCompanies = () => {
    const { data } = useGetCompanies({ refetchOnMount: false });

    return useMemo(() => {
        const companies = (data?.items || []).map((company) => {
            if (!company.name) {
                return {
                    ...company,
                    name: messages.unnamedCompanyName,
                };
            }

            return company;
        });

        return arrayHelpers.arraySort(
            companies,
            compareHelpers.comparatorFor(compareHelpers.stringComparator2AscI, 'name')
        );
    }, [data?.items]);
};

export const useCompany = () => {
    const activeCompanyId = useAtomValue(activeCompanyIdState);

    return useGetCompany({ path: { companyId: activeCompanyId } });
};

const useCountMenuClickCallback = (params: UseCountMenuClickCallbackParams) => {
    const { sessionKeyShowCount, sessionKeyShowTime, limitChangeOfUrl, limitShowInSession, onOpen } = params;

    const [menuClickCount, setMenuClickCount] = useState(0);

    const handleMenuClick = useCallback(() => {
        const countFromStorage = sessionStorage.getItem(sessionKeyShowCount);

        let showInSessionCount = countFromStorage ? parseInt(countFromStorage, 10) || 0 : 0;

        if (showInSessionCount >= limitShowInSession) {
            if (!isEnoughDaysAfter(2, sessionStorage.getItem(sessionKeyShowTime))) {
                return;
            } else {
                showInSessionCount = 0;
                sessionStorage.removeItem(sessionKeyShowCount);
            }
        }

        if (menuClickCount >= limitChangeOfUrl - 1) {
            setMenuClickCount(0);

            onOpen();
            sessionStorage.setItem(sessionKeyShowCount, String(showInSessionCount + 1));
            sessionStorage.setItem(sessionKeyShowTime, String(Date.now()));

            return;
        }

        setMenuClickCount(menuClickCount + 1);
    }, [limitChangeOfUrl, limitShowInSession, menuClickCount, onOpen, sessionKeyShowCount, sessionKeyShowTime]);

    return handleMenuClick;
};

const useSubscribeAfterTrialPopup = (company?: Company, me?: CompanyMe) => {
    const setSubscribeAfterTrialPopupOpen = useSetAtom(subscribeAfterTrialPopupOpenState);

    const canShowSubscribeAfterTrialButton = useMemo(
        () => getCanShowSubscribeAfterTrialButtonV2(company, me),
        [company, me]
    );

    const handleOpen = useCallback(() => {
        setSubscribeAfterTrialPopupOpen(true);
    }, [setSubscribeAfterTrialPopupOpen]);

    const handleMenuClick = useCountMenuClickCallback({
        sessionKeyShowCount: sessionKeyAfterTrialBigPopupShowCount,
        sessionKeyShowTime: sessionKeyAfterTrialBigPopupShowTime,
        limitChangeOfUrl: 3,
        limitShowInSession: 3,
        onOpen: handleOpen,
    });

    return canShowSubscribeAfterTrialButton ? handleMenuClick : undefined;
};

const usePushingConnectGLPopup = (company?: Company, me?: CompanyMe) => {
    const [showCount, setShowCount] = useState(() => {
        const storageCount = localStorage.getItem(storageKeyPushingConnectGLShowCount);

        return storageCount ? parseInt(storageCount, 10) || 0 : 0;
    });

    const [isPushingConnectGLPopupHide, setPushingConnectGLPopupHide] = useAtom(pushingConnectGLPopupHideState);
    const setPushingConnectGLPopupState = useSetAtom(pushingConnectGLPopupState);

    const canShowPushingConnectGLPopup = useMemo(
        () => showCount < maxShowCountForPushingConnectGLPopup && getCanShowPushingConnectGLPopupV2(company, me),
        [showCount, company, me]
    );

    const handleOpen = useCallback(() => {
        if (company && me) {
            setPushingConnectGLPopupState({ companyId: company?.id, profileId: me?.id });

            const nextCount = showCount + 1;

            if (nextCount >= maxShowCountForPushingConnectGLPopup) {
                localStorage.removeItem(storageKeyPushingConnectGLShowCount);
                setPushingConnectGLPopupHide(true);
            } else {
                setShowCount(nextCount);
                localStorage.setItem(storageKeyPushingConnectGLShowCount, String(nextCount));
            }
        }
    }, [company, me, setPushingConnectGLPopupHide, setPushingConnectGLPopupState, showCount]);

    const handleMenuClick = useCountMenuClickCallback({
        sessionKeyShowCount: sessionKeyPushingConnectGLShowCount,
        sessionKeyShowTime: sessionKeyPushingConnectGLShowTime,
        limitChangeOfUrl: 2,
        limitShowInSession: 2,
        onOpen: handleOpen,
    });

    return !isPushingConnectGLPopupHide && canShowPushingConnectGLPopup ? handleMenuClick : undefined;
};

export const useHandleMenuClick = (company?: Company, me?: CompanyMe) => {
    const { isDisablePromoPopups } = useUserBetaFeatures();

    const handleSubscribeAfterTrialPopup = useSubscribeAfterTrialPopup(company, me);
    const handlePushingConnectGLPopup = usePushingConnectGLPopup(company, me);

    if (isDisablePromoPopups) {
        return undefined;
    }

    return handleSubscribeAfterTrialPopup ?? handlePushingConnectGLPopup;
};
